import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    AlertTriangle,
    Sparkles,
    Globe,
    FileSearch,
    Smartphone,
    PenTool,
    Code2,
    FileText,
    FileSpreadsheet,
    BookOpen,
    Scale,
    Building2,
    Landmark,
    Flag,
    MapPin,
    BadgeCheck,
    GraduationCap,
    LineChart,
    FileCheck,
    Users,
    Wrench,
    Languages,
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

/* ---------------- HERO ---------------- */
const Hero = () => (
    <section className="relative overflow-hidden border-b border-brand-border" aria-labelledby="services-hero">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-white to-[#F1F7F8]" aria-hidden="true" />
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#127A75]/10 blur-3xl -z-10" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Services</p>
            <h1 id="services-hero" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-text tracking-tight max-w-4xl">
                Accessibility Services Built for Global Compliance
            </h1>
            <p className="mt-5 text-lg text-brand-body max-w-3xl">
                End-to-end accessibility services covering web, mobile, documents, and design — aligned with every major global standard.
            </p>
        </div>
    </section>
);

/* ---------------- DATA ---------------- */
const CATEGORIES = [
    {
        id: "web-mobile",
        label: "Web & Mobile",
        title: "Web & Mobile Accessibility Audits",
        services: [
            {
                icon: FileSearch,
                title: "WCAG 2.1 & 2.2 Audits",
                bullets: [
                    "Manual testing + automated scanning + screen reader testing (NVDA, JAWS, VoiceOver, TalkBack)",
                    "Keyboard navigation testing",
                    "Mobile accessibility testing (iOS, Android)",
                    "Detailed report with severity ratings, remediation guidance, code examples",
                ],
                meta: "Timeline: 1–3 weeks based on site size",
            },
            {
                icon: Smartphone,
                title: "Mobile App Accessibility Audits",
                bullets: [
                    "iOS — VoiceOver, Dynamic Type, Switch Control",
                    "Android — TalkBack, Accessibility Scanner",
                    "React Native, Flutter, native apps",
                    "Apple HIG and Material Design accessibility guidelines",
                ],
            },
            {
                icon: PenTool,
                title: "Design Accessibility Audit (pre-development)",
                bullets: [
                    "Figma / Sketch / Adobe XD design file review",
                    "Color contrast, touch target sizing, focus order planning",
                    "Component-level accessibility annotations",
                    "Annotated design files + design system accessibility guidelines",
                ],
                meta: "Catch issues at design stage — 10× cheaper than post-launch fixes",
            },
            {
                icon: Code2,
                title: "Code-Level Accessibility Review",
                bullets: [
                    "HTML semantics and ARIA implementation review",
                    "Component library audits (React, Vue, Angular)",
                    "Pull request reviews for ongoing development teams",
                ],
            },
        ],
    },
    {
        id: "documents",
        label: "Documents",
        title: "Document Accessibility Remediation",
        services: [
            {
                icon: FileText,
                title: "PDF Accessibility Remediation",
                bullets: [
                    "Tagged PDF creation, reading order, alt text",
                    "Form field labeling, tab order, table accessibility",
                    "Bookmarks, navigation, language tags",
                    "Tools: Adobe Acrobat Pro DC, CommonLook, axesPDF, PAC 2024",
                ],
                meta: "PDF/UA (ISO 14289), WCAG 2.1/2.2, Section 508, HHS standards",
            },
            {
                icon: FileSpreadsheet,
                title: "Microsoft Office Document Accessibility",
                bullets: [
                    "Word — styles, headings, alt text, accessible tables",
                    "PowerPoint — reading order, slide titles, alt text",
                    "Excel — sheet names, table structure, cell formatting",
                    "Tools: Microsoft Accessibility Checker, CommonLook Office",
                ],
            },
            {
                icon: BookOpen,
                title: "EPUB & Digital Publication Accessibility",
                bullets: [
                    "EPUB 3 accessibility for publishers",
                    "Educational content — textbooks and learning materials",
                    "WCAG + EPUB Accessibility 1.1 conformance",
                ],
            },
        ],
    },
    {
        id: "compliance",
        label: "Compliance",
        title: "Compliance & Certification",
        services: [
            {
                icon: Scale,
                title: "ADA Compliance (US)",
                badge: { text: "High Risk", tone: "warning" },
                bullets: [
                    "For US businesses facing legal risk",
                    "Title III ADA compliance",
                    "Demand letter response support",
                    "Settlement remediation roadmaps",
                ],
            },
            {
                icon: Sparkles,
                title: "EAA Compliance (Europe)",
                badge: { text: "New Regulation", tone: "info" },
                bullets: [
                    "European Accessibility Act enforced from June 2025",
                    "E-commerce, banking, transport, e-books, ATMs, ticketing",
                    "Full EN 301 549 conformance audits",
                ],
            },
            {
                icon: Landmark,
                title: "Section 508 (US Government & Contractors)",
                bullets: [
                    "For federal contractors and government suppliers",
                    "Revised Section 508 (ICT Refresh) compliance",
                    "Trusted Tester methodology applied where requested",
                ],
            },
            {
                icon: Flag,
                title: "AODA Compliance (Canada)",
                bullets: [
                    "Accessibility for Ontarians with Disabilities Act",
                    "WCAG 2.0 Level AA + Ontario-specific requirements",
                    "AODA compliance reports for Ontario businesses",
                ],
            },
            {
                icon: Building2,
                title: "BITV 2.0 Compliance (Germany)",
                bullets: [
                    "Barrierefreie-Informationstechnik-Verordnung",
                    "Public sector requirements in Germany",
                    "BITV-Test methodology",
                ],
            },
            {
                icon: MapPin,
                title: "RGAA Compliance (France)",
                bullets: [
                    "Référentiel Général d'Amélioration de l'Accessibilité",
                    "French public sector accessibility law",
                    "RGAA 4 conformance audits",
                ],
            },
            {
                icon: Globe,
                title: "Other Global Standards",
                bullets: [
                    "DDA (UK, Australia), Equality Act 2010 (UK)",
                    "Israeli Standard 5568, JIS X 8341 (Japan)",
                    "Norwegian DTL, Stanca Act (Italy)",
                    "Indian RPWD Act 2016 + GIGW guidelines, Accessible Canada Act",
                ],
            },
            {
                icon: FileCheck,
                title: "VPAT / ACR Reports",
                bullets: [
                    "VPAT 2.5 — Rev 508, WCAG, EU, INT versions",
                    "Accessibility Conformance Reports for procurement",
                    "Required for B2B SaaS selling to enterprises and government",
                ],
            },
        ],
    },
    {
        id: "support",
        label: "Remediation & Training",
        title: "Remediation, Training & Ongoing Support",
        services: [
            {
                icon: Wrench,
                title: "Remediation Support",
                bullets: [
                    "Code-level fixes",
                    "Developer pair-programming",
                    "Component library remediation",
                    "CMS-specific fixes (WordPress, Drupal, Shopify, Magento)",
                ],
            },
            {
                icon: GraduationCap,
                title: "Accessibility Training",
                bullets: [
                    "Developer training (HTML, ARIA, JavaScript)",
                    "Designer training (inclusive design principles)",
                    "Content author training (accessible content creation)",
                    "QA team training (accessibility testing methodologies)",
                ],
            },
            {
                icon: LineChart,
                title: "Ongoing Monitoring & Maintenance",
                bullets: [
                    "Quarterly re-audits",
                    "Continuous compliance dashboards",
                    "New feature accessibility reviews",
                    "Regression testing on releases",
                ],
            },
            {
                icon: Users,
                title: "Accessibility Policy & Statement Drafting",
                bullets: [
                    "Custom accessibility statements",
                    "Internal accessibility policies",
                    "Procurement accessibility requirements documentation",
                ],
            },
        ],
    },
];

const TOOL_GROUPS = [
    { label: "PDFs", items: ["Adobe Acrobat Pro DC", "CommonLook PDF", "CommonLook Office", "axesPDF", "PAC 2024"] },
    { label: "Web", items: ["axe DevTools", "WAVE", "ARC Toolkit", "Lighthouse", "Accessibility Insights"] },
    { label: "Screen Readers", items: ["NVDA", "JAWS", "VoiceOver", "TalkBack", "Narrator"] },
    { label: "Mobile", items: ["Accessibility Inspector (iOS)", "Accessibility Scanner (Android)"] },
    { label: "Design", items: ["Stark", "Figma A11y annotations", "Adobe XD accessibility tools"] },
];

/* ---------------- BADGE ---------------- */
const Badge = ({ tone, children }) => {
    const palette = {
        warning: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
        info: "bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${palette[tone] || palette.info}`}>
            {tone === "warning" && <AlertTriangle size={12} aria-hidden="true" />}
            {tone === "info" && <Sparkles size={12} aria-hidden="true" />}
            {children}
        </span>
    );
};

/* ---------------- SERVICE CARD ---------------- */
const ServiceCard = ({ service, index }) => {
    const Icon = service.icon;
    return (
        <Card
            className="h-full p-7 border-brand-border hover:shadow-cardHover transition-shadow duration-200 bg-white"
            data-testid={`service-card-${index}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="w-11 h-11 rounded-md bg-brand-soft text-brand-teal inline-flex items-center justify-center flex-none">
                    <Icon size={22} aria-hidden="true" />
                </div>
                {service.badge && <Badge tone={service.badge.tone}>{service.badge.text}</Badge>}
            </div>
            <h3 className="mt-5 text-lg font-bold text-brand-text">{service.title}</h3>
            <ul className="mt-3 space-y-2 text-sm text-brand-body leading-relaxed">
                {service.bullets.map((b, i) => (
                    <li key={i} className="flex gap-2">
                        <span aria-hidden="true" className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-brand-teal flex-none" />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            {service.meta && (
                <p className="mt-4 pt-4 border-t border-brand-border text-xs font-semibold uppercase tracking-wider text-brand-muted">
                    {service.meta}
                </p>
            )}
        </Card>
    );
};

/* ---------------- CATEGORY TABS ---------------- */
const CategoryTabs = () => (
    <section aria-labelledby="services-categories" className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 id="services-categories" className="sr-only">Service categories</h2>
            <Tabs defaultValue={CATEGORIES[0].id} className="w-full">
                <TabsList
                    className="flex flex-wrap w-full h-auto justify-start gap-2 bg-brand-soft p-2 rounded-lg border border-brand-border"
                    data-testid="services-tabs-list"
                >
                    {CATEGORIES.map((c) => (
                        <TabsTrigger
                            key={c.id}
                            value={c.id}
                            className="flex-1 min-w-[140px] min-h-[44px] rounded-md px-4 py-3 text-sm font-semibold text-brand-text data-[state=active]:bg-brand-navy data-[state=active]:text-white data-[state=active]:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tealBright focus-visible:ring-offset-2"
                            data-testid={`services-tab-${c.id}`}
                        >
                            {c.label}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {CATEGORIES.map((c) => (
                    <TabsContent key={c.id} value={c.id} className="mt-10" id={`category-${c.id}`}>
                        <h3 className="text-2xl sm:text-3xl font-bold text-brand-text">{c.title}</h3>
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {c.services.map((s, i) => (
                                <ServiceCard key={s.title} service={s} index={`${c.id}-${i}`} />
                            ))}
                        </div>
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    </section>
);

/* ---------------- TOOLS ---------------- */
const Tools = () => (
    <section aria-labelledby="services-tools" className="bg-brand-soft border-y border-brand-border py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Tools & methodology</p>
            <h2 id="services-tools" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">
                Industry-standard tools, expert-led methodology.
            </h2>
            <p className="mt-3 text-base text-brand-body max-w-3xl">
                We use the same professional toolchain trusted by accessibility teams at Fortune 500s and government agencies — combined with manual expert testing.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {TOOL_GROUPS.map((g) => (
                    <Card key={g.label} className="p-6 bg-white border-brand-border" data-testid={`tools-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">For {g.label}</p>
                        <ul className="mt-3 flex flex-wrap gap-2">
                            {g.items.map((tool) => (
                                <li key={tool}>
                                    <span className="inline-flex items-center rounded-md bg-brand-soft border border-brand-border px-3 py-1.5 text-sm font-medium text-brand-text">
                                        {tool}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                ))}
            </div>
        </div>
    </section>
);

/* ---------------- PRICING CTA ---------------- */
const PricingCTA = () => (
    <section aria-labelledby="services-pricing" className="py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <Languages className="mx-auto text-brand-teal" size={36} aria-hidden="true" />
            <h2 id="services-pricing" className="mt-5 text-3xl sm:text-4xl font-bold text-brand-text">
                Pricing that fits your scope.
            </h2>
            <p className="mt-4 text-lg text-brand-body">
                Pricing is project-based and depends on scope, complexity, and standards required. Get a custom quote tailored to your platform.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-navy text-white font-semibold px-6 py-3 hover:bg-brand-teal transition-colors duration-200 touch-target"
                    data-testid="services-cta-quote"
                >
                    Request a Quote
                    <ArrowRight size={18} aria-hidden="true" />
                </Link>
                <Link
                    to="/free-audit"
                    className="inline-flex items-center justify-center rounded-md border-2 border-brand-navy text-brand-navy font-semibold px-6 py-3 hover:bg-brand-navy hover:text-white transition-colors duration-200 touch-target"
                    data-testid="services-cta-audit"
                >
                    Get a Free Mini Audit
                </Link>
            </div>
        </div>
    </section>
);

/* ---------------- PAGE ---------------- */
export default function Services() {
    useEffect(() => {
        document.title = "Services — AccessNova";
    }, []);

    return (
        <>
            <Hero />
            <CategoryTabs />
            <Tools />
            <PricingCTA />
        </>
    );
}
